import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { AppState, Interval } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { median } from 'app/utils/math';
import User from 'entity/User';
import {
  OutgoingMessageType,
  OverallStatsMessage,
  PublishStatsMessage,
  StatsDimension,
  StatsRecord,
  StatsTimeType,
  StatsValueType,
  SubscriptionStatsMessage,
} from 'messages/websocket.messages';

import { formatPeerStats } from '../../preciate-stream/peer-connection-stats-helper';
import { socialAppSelectors } from '../../state/app';
import { SubscriptionType } from '../subscription/subscription.types';
import { WebsocketService } from '../websocket.service';

import { PublisherType } from './types';

const PEER_STATS_MONITOR_INTERVAL_MS = 10000;

interface ReportResult {
  records: StatsRecord[];
  duration: number;
}

interface TypedSubscriberPeer {
  type: SubscriptionType;
  peer: RTCPeerConnection;
}

interface TypedPublisherPeer {
  type: PublisherType;
  peer: RTCPeerConnection;
}

/**
 * Service for collecting peer connection stats and relaying them to the server via messaging.
 */
@Injectable({
  providedIn: 'root',
})
export class RtcStatsStreamService extends Loggable {
  private websocketService = inject(WebsocketService);
  private store = inject<Store<AppState>>(Store);

  private publishers = new Map<PublisherType, TypedPublisherPeer>();
  private subscriptions = new Map<string, TypedSubscriberPeer>();
  private statsInterval?: Interval;
  private user?: User;
  private isMonitoringEnabled = false;
  private isJoined = false;
  private counters: Record<string, number> = {};

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('RtcStatsStreamService');
  }

  public init() {
    this.info('RtcStatsStreamService initialized');
    this.destroyed$ = new ReplaySubject<boolean>(1);

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.peer_stats_monitoring)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isMonitoringEnabled) => (this.isMonitoringEnabled = isMonitoringEnabled));

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => (this.user = user));

    this.store
      .select(socialAppSelectors.isJoined)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isJoined) => (this.isJoined = isJoined));

    this.statsInterval = setInterval(async (): Promise<void> => {
      if (!this.isJoined) {
        return;
      }

      if (!this.isMonitoringEnabled) {
        return;
      }

      this.monitorOverallStats();
      await this.monitorPublishers();
      await this.monitorSubscriptions();
    }, PEER_STATS_MONITOR_INTERVAL_MS);
  }

  public destroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();

    // Reset accumulated state
    this.counters = {};
    this.publishers.clear();
    this.subscriptions.clear();

    if (this.statsInterval) {
      clearInterval(this.statsInterval);
    }
    this.statsInterval = undefined;

    this.info('RtcStatsStreamService destroyed');
  }

  /**
   * Begin monitoring a publisher's stats.
   * @param type The type of publisher to monitor
   * @param peer The publisher peer.
   */
  public addPublishMonitoring(type: PublisherType, peer: RTCPeerConnection): void {
    this.publishers.set(type, { type, peer });
  }

  /**
   * Stop monitoring a publisher's stats.
   */
  public removePublishMonitoring(type: PublisherType): void {
    this.publishers.delete(type);
  }

  /**
   * Begin monitoring a subscriber's stats.
   * @param peerId A unique identifier of the subscription
   * @param type The type of subscriber to monitor
   * @param peer The subscriber peer.
   */
  public addSubscriptionMonitoring(peerId: string, type: SubscriptionType, peer: RTCPeerConnection): void {
    this.subscriptions.set(peerId, { type, peer });
  }

  /**
   * Stop monitoring a subscribers's stats.
   */
  public removeSubscriptionMonitoring(peerId: string): void {
    this.subscriptions.delete(peerId);
  }

  private monitorOverallStats(): void {
    if (!this.user) {
      throw new Error('User is not defined');
    }
    assertDefined(this.user.userId);
    const dimensions: StatsDimension[] = [{ Name: 'userId', Value: this.user.userId }];
    const peerStatCount: Record<SubscriptionType, number> = {
      [SubscriptionType.LIVE_STREAM]: 0,
      [SubscriptionType.MIRROR]: 0,
      [SubscriptionType.MIX]: 0,
      [SubscriptionType.PARTICIPANT]: 0,
      [SubscriptionType.SCREEN_SHARE]: 0,
    };

    if (!this.subscriptions) {
      return;
    }

    this.subscriptions.forEach((subscription) => {
      peerStatCount[subscription.type]++;
    });
    const timestamp = Date.now();

    const records: StatsRecord = {
      MeasureName: 'overallStats',
      Dimensions: dimensions,
      MeasureValueType: StatsValueType.MULTI,
      Time: Math.round(timestamp).toString(),
      TimeUnit: StatsTimeType.MILLISECONDS,
      MeasureValues: [
        {
          Name: 'liveStreamSubscriptions',
          Value: peerStatCount.LIVE_STREAM.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'mirrorSubscriptions',
          Value: peerStatCount.MIRROR.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'mixSubscriptions',
          Value: peerStatCount.MIX.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'participantSubscriptions',
          Value: peerStatCount.PARTICIPANT.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'screenShareSubscriptions',
          Value: peerStatCount.SCREEN_SHARE.toString(),
          Type: StatsValueType.BIGINT,
        },
      ],
    };
    // Append counters
    if (!Object.entries(this.counters).length) {
      return;
    }
    Object.entries(this.counters).forEach(([name, value]) => {
      records.MeasureValues?.push({ Name: name, Value: value.toString(), Type: StatsValueType.BIGINT });
    });

    const msg: OverallStatsMessage = {
      t: OutgoingMessageType.OVERALL_STATS,
      Records: [records],
    };
    this.websocketService.sendMessage(msg);
  }

  /**
   * Increment a counter value.
   * @param name The name of the counter to increment.
   */
  public incrementCounter(name: string, value = 1) {
    this.counters[name] = (this.counters[name] ?? 0) + value;
  }

  /**
   * Clear a counter value.
   * @param name The name of the counter to clear.
   */
  public clearCounter(name: string) {
    delete this.counters[name];
  }

  private async monitorPublishers(): Promise<void> {
    let records: StatsRecord[] = [];
    for (const { peer, type } of this.publishers.values()) {
      const newRecords = await this.monitorPublisher(type, peer);
      records = records.concat(newRecords);
    }

    if (records.length === 0) {
      return;
    }

    const msg: PublishStatsMessage = {
      t: OutgoingMessageType.PUB_STATS,
      Records: records,
    };
    this.websocketService.sendMessage(msg);
  }

  private async monitorPublisher(type: PublisherType, peer: RTCPeerConnection): Promise<StatsRecord> {
    if (!this.user) {
      throw new Error('User is not defined');
    }
    assertDefined(this.user.userId);
    const dimensions: StatsDimension[] = [
      { Name: 'userId', Value: this.user.userId },
      { Name: 'type', Value: type },
    ];

    const stats = await peer?.getStats();

    const peerStats = formatPeerStats(stats);
    return {
      Dimensions: dimensions,
      MeasureName: 'publisherStats',
      MeasureValueType: StatsValueType.MULTI,
      Time: Math.round(peerStats.timestamp).toString(),
      TimeUnit: StatsTimeType.MILLISECONDS,
      MeasureValues: [
        {
          Name: 'bytesSent',
          Value: peerStats.bytesSent.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'packetsSent',
          Value: peerStats.packetsSent.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'targetBitrate',
          Value: peerStats.targetBitrate.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'nackCount',
          Value: peerStats.nackCount.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'pliCount',
          Value: peerStats.pliCount.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'firCount',
          Value: peerStats.firCount.toString(),
          Type: StatsValueType.BIGINT,
        },
      ],
    };
  }

  private async monitorSubscriptions(): Promise<void> {
    let subscriptionRecords: StatsRecord[] = [];
    const subscriptionMeasurementDurations: number[] = [];
    for (const [subscriptionId, subscriberPeer] of this.subscriptions.entries()) {
      const { duration, records } = await this.monitorSubscription(subscriptionId, subscriberPeer.peer);
      subscriptionMeasurementDurations.push(duration);
      subscriptionRecords = subscriptionRecords.concat(records);
    }

    const medianDuration = median(subscriptionMeasurementDurations);
    this.info('Subscription monitoring duration:', {
      medianDuration,
      subscriptionCount: subscriptionMeasurementDurations.length,
    });
    if (subscriptionRecords.length === 0) {
      return;
    }

    const msg: SubscriptionStatsMessage = {
      t: OutgoingMessageType.SUB_STATS,
      Records: subscriptionRecords,
    };
    this.websocketService.sendMessage(msg);
  }

  private async monitorSubscription(subscriptionId: string, peer: RTCPeerConnection): Promise<ReportResult> {
    if (!this.user) {
      throw new Error('User is not defined');
    }
    assertDefined(this.user.userId);
    const dimensions: StatsDimension[] = [
      { Name: 'subscriptionId', Value: subscriptionId },
      { Name: 'userId', Value: this.user.userId },
    ];

    const startReportTime = Date.now();
    const stats = await peer.getStats();
    const endReportTime = Date.now();
    const measureDuration = endReportTime - startReportTime;

    const peerStats = formatPeerStats(stats);
    const record: StatsRecord = {
      Dimensions: dimensions,
      MeasureName: 'subscriptionStats',
      MeasureValueType: StatsValueType.MULTI,
      Time: Math.round(peerStats.timestamp).toString(),
      TimeUnit: StatsTimeType.MILLISECONDS,
      MeasureValues: [
        {
          Name: 'bytesReceived',
          Value: peerStats.bytesReceived.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'packetsReceived',
          Value: peerStats.packetsReceived.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'packetsLost',
          Value: peerStats.packetsLost.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'audioPacketsLost',
          Value: peerStats.audioPacketsLost.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'audioJitter',
          Value: peerStats.audioJitter.toString(),
          Type: StatsValueType.DOUBLE,
        },
        {
          Name: 'videoJitter',
          Value: peerStats.videoJitter.toString(),
          Type: StatsValueType.DOUBLE,
        },
        {
          Name: 'videoPacketsLost',
          Value: peerStats.videoPacketsLost.toString(),
          Type: StatsValueType.BIGINT,
        },
        {
          Name: 'statsDuration',
          Value: measureDuration.toString(),
          Type: StatsValueType.BIGINT,
        },
      ],
    };
    // Return the duration of the report in milliseconds.
    return { records: [record], duration: measureDuration };
  }
}
